import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../config/axiosConfig";
import { useSelector } from "react-redux";

export const fetchPagos = async (id_empresa) => {
  if (!id_empresa) {
    console.warn("⚠️ `id_empresa` no es válido en fetchTerceros:", id_empresa);
    return [];
  }
  try {
    const { data } = await api.get(`/${id_empresa}/pagos`);
    return data;
  } catch (error) {
    console.error("❌ Error en la petición:", error);
    return [];
  }
};

export const usePagos = () => {
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id; // ✅ Asegurar que existe un `id_empresa`

  return useQuery({
    queryKey: ["pagos", id_empresa], // Clave para el caché
    queryFn: () => (id_empresa ? fetchPagos(id_empresa) : Promise.resolve([])),
    enabled: !!id_empresa,
  });
};

export const savePago = async (pagoData, id_empresa) => {
  if (!id_empresa) throw new Error("ID de empresa no válido");
  if (pagoData.id) {
    // Actualizar
    const { data } = await api.put(`/${id_empresa}/pagos/${pagoData.id}`, pagoData);
    return data;
  } else {
    // Crear nuevo
    const { data } = await api.post(`/${id_empresa}/pagos`, pagoData);
    return data;
  }
};

export const useSavePago = () => {
  const queryClient = useQueryClient();
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id;

  return useMutation({
    mutationFn: (pagoData) => savePago(pagoData, id_empresa),

    onSuccess: (savedPago) => {
      // Actualizar caché según si es nuevo o editado
      queryClient.setQueryData(["pagos", id_empresa], (oldData) => {
        if (!oldData) return [savedPago];
        const exists = oldData.some((p) => String(p.id) === String(savedPago.id));
        if (exists) {
          return oldData.map((p) => (String(p.id) === String(savedPago.id) ? savedPago : p));
        } else {
          return [savedPago, ...oldData];
        }
      });
    },
  });
};

export const usePagoById = (id_pago) => {
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id;

  return useQuery({
    queryKey: ["pago", id_empresa, id_pago],
    queryFn: async () => {
      try {
        const { data } = await api.get(`/${id_empresa}/pagos/${id_pago}`);
        return data;
      } catch (error) {
        console.error("❌ Error al obtener pago:", error);
        return {};
      }
    },
    enabled: !!id_empresa && !!id_pago,
  });
};

export const deletePago = async (id_pago, id_empresa) => {
  if (!id_empresa) throw new Error("ID de empresa no válido");
  await api.delete(`/${id_empresa}/pagos/${id_pago}`);
  return id_pago; // Retornar el id eliminado para usarlo en el cache
};

export const useDeletePago = () => {
  const queryClient = useQueryClient();
  const { empresaSeleccionada } = useSelector((state) => state.auth);
  const id_empresa = empresaSeleccionada?.id;

  return useMutation({
    mutationFn: (id_pago) => deletePago(id_pago, id_empresa),

    onSuccess: (id_pago_eliminado) => {
      // Actualizar lista de pagos en caché
      queryClient.setQueryData(["pagos", id_empresa], (oldData) => {
        if (!oldData) return [];
        return oldData.filter((pago) => pago.id !== id_pago_eliminado);
      });
    },

    onError: (error) => {
      console.error("❌ Error al eliminar el pago:", error);
    },
  });
};
